import { ref } from 'vue';
import { route } from 'ziggy-js';

const loading = ref({});
const ledger = ref({});
const automaticallyLoadedEvents = new Set();

const getLedger = async (eventId = null) => {
    eventId = eventId || route().params.event; // Get eventId from route if not provided
    if (!eventId) {
        console.error('Event ID is required to fetch ledger data.');
        return;
    }
    loading.value[eventId] = true;
    try {
        const response = await fetch(route('api.account.event.ledger', { event: eventId }));
        if (!response.ok) {
            throw new Error('Failed to fetch ledger data');
        }
        const data = await response.json();
        ledger.value[eventId] = data;
    } catch (error) {
        console.error('Error fetching ledger:', error);
    } finally {
        loading.value[eventId] = false;
    }
};

const getFigureValue = (category, figureKey) => {
    const figure = figureKey.split('.').reduce((value, key) => value?.[key], category);

    return Number(figure) || 0;
};

const loadLedgerIfMissing = (eventId) => {
    if (
        !eventId ||
        ledger.value[eventId] !== undefined ||
        loading.value[eventId] ||
        automaticallyLoadedEvents.has(eventId)
    ) {
        return;
    }

    automaticallyLoadedEvents.add(eventId);
    void getLedger(eventId);
};

const getCategoryFigure = (eventId, departmentId, categoryId, figureKey) => {
    loadLedgerIfMissing(eventId);

    const category = ledger.value[eventId]?.[departmentId]?.[categoryId];

    return getFigureValue(category, figureKey);
};

const getDepartmentFigure = (eventId, departmentId, figureKey) => {
    loadLedgerIfMissing(eventId);

    const categories = ledger.value[eventId]?.[departmentId] ?? {};

    return Object.values(categories).reduce((total, category) => total + getFigureValue(category, figureKey), 0);
};

const getEventFigure = (eventId, figureKey) => {
    loadLedgerIfMissing(eventId);

    const departments = ledger.value[eventId] ?? {};

    return Object.values(departments).reduce(
        (eventTotal, categories) =>
            eventTotal +
            Object.values(categories).reduce(
                (departmentTotal, category) => departmentTotal + getFigureValue(category, figureKey),
                0
            ),
        0
    );
};

export { loading, ledger, getLedger, getCategoryFigure, getDepartmentFigure, getEventFigure };
